import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  Pagination,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { ReactComponent as EyeIcon } from "../../../assets/svg/eye.svg";
import { useNavigate } from "react-router-dom";
import { getOrganization } from "../../../api/organization";
import CommonHeader from "../../../CommonComponents/CommonHeader";
import SearchInput from "../../../CommonComponents/SearchInput";
import ButtonCommon from "../../../CommonComponents/ButtonCommon";
import { Add } from "@mui/icons-material";
import SelectCommon from "../../../CommonComponents/SelectCommon";
import DateFilterPicker from "../../../CommonComponents/DateFilterPicker";
import ComponentWrapper from "../../../CommonComponents/ComponentWrapper";
// import { showErrorToast, showSuccessToast } from '../../../toastUtils';

export default function Organizations() {
  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: "#F9FAFB",
      color: "#000000",
      fontWeight: 600,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
  }));

  const navigate = useNavigate();
  const [organizationData, setOrganizationData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState("");
  const [dateValue, setDateValue] = useState(null);
  const [statusValue, setStatusValue] = useState("all");
  const [filteredData, setFilteredData] = useState([]);
  const [paginatedData, setPaginatedData] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    const fetchUserData = async () => {
      setLoading(true);
      try {
        const response = await getOrganization(page, pageSize);
        if (response.success) {
          setOrganizationData(response.data.organizations || []);
          setFilteredData(response.data.organizations || []);
          setPaginatedData(response.data.organizations || []);
          setTotalCount(response.data.total_count || 0);
        }
      } catch (error) {
        console.error("Error fetching organization data:", error);
        setPaginatedData([]);
        setFilteredData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let data = paginatedData?.length ? paginatedData : [];

    const filtered = data.filter((item) => {
      const orgName = item.organization_name?.toLowerCase() || "";
      const planName = item.subscription_details.plan_name?.toLowerCase() || "";
      const search = searchValue?.toLowerCase() || "";

      const isOrgNameMatch = orgName.includes(search);
      const isPlanNameMatch = planName.includes(search);

      const isDateMatch = dateValue
        ? new Date(item.subscription_details.expiry_date).toDateString() ===
        new Date(dateValue).toDateString()
        : true;

      const isStatusMatch =
        statusValue === "all"
          ? true
          : item.subscription_details.subscription_status.toLowerCase() ===
          statusValue.toLowerCase();

      return (
        (isOrgNameMatch || isPlanNameMatch) && isDateMatch && isStatusMatch
      );
    });

    setFilteredData(filtered);
  }, [searchValue, dateValue, statusValue, organizationData, paginatedData]);

  return (
    <ComponentWrapper>
      {/* <Stack sx={{ maxWidth: "1000px" }}> */}
      <Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
          <CommonHeader
            title="Organization"
            subTitle="You can add, deactivate or activate the organizations on this page."
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "end",
            gap: 2,
            mb: 2,
          }}
        >
          <SearchInput
            placeholder="Search by Name and Plan"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
          />
          <DateFilterPicker
            value={dateValue}
            onChange={setDateValue}
            sx={{ maxWidth: 250 }}
            label="Filter by Date"
          />
          <SelectCommon
            label="Status"
            value={statusValue}
            onChange={(e) => setStatusValue(e.target.value)}
            options={[
              { label: "All", value: "all" },
              { label: "Active", value: "active" },
              { label: "Inactive", value: "inactive" },
              { label: "Deactive", value: "deactive" },
            ]}
          />
          <ButtonCommon
            icon={<Add />}
            onClick={() => navigate("/customer/organizations/create")}
          >
            Create
          </ButtonCommon>
        </Box>
      </Box>

      <Box>
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{ borderRadius: "12px" }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableCell>Organization Name</StyledTableCell>
                <StyledTableCell>Plan</StyledTableCell>
                <StyledTableCell>Expiry Date</StyledTableCell>
                <StyledTableCell>Status</StyledTableCell>
                <StyledTableCell>Action</StyledTableCell>
              </TableRow>
            </TableHead>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : (
              <TableBody>
                {filteredData && filteredData.length > 0 ? (
                  filteredData.map((row) => (
                    <TableRow
                      key={row.organization_id}
                      hover
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                        verticalAlign: "middle",
                      }}
                    >
                      <StyledTableCell>{row.organization_name}</StyledTableCell>
                      <StyledTableCell>
                        {row.subscription_details.plan_name}
                      </StyledTableCell>
                      <StyledTableCell>
                        {row.subscription_details.expiry_date}
                      </StyledTableCell>
                      <StyledTableCell
                        sx={{
                          textTransform: "capitalize",
                          color:
                            row.subscription_details.subscription_status === "active"
                              ? "#00B894"
                              : row.subscription_details.subscription_status === "inactive"
                                ? "#FF946D" : "#6994FF",
                          fontWeight: 600,
                        }}
                      >
                        {row.subscription_details.subscription_status === 'deactive' ? 'Deactivated' : row.subscription_details.subscription_status}
                      </StyledTableCell>
                      <StyledTableCell sx={{ px: 0 }}>
                        <Tooltip title="View" arrow>
                          <IconButton
                            sx={{ ml: 2 }}
                            onClick={() =>
                              navigate(
                                `/customer/organizations/view/${row.organization_id}`
                              )
                            }
                          >
                            <EyeIcon />
                          </IconButton>
                        </Tooltip>

                        {/* <Tooltip title="Delete" arrow onClick={() => handleDeleteOrganization(row.organization_id)}>
                        <IconButton>
                          <Trash />
                        </IconButton>
                      </Tooltip> */}
                      </StyledTableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <StyledTableCell colSpan={5}>
                      <Typography
                        sx={{
                          textAlign: "center",
                          py: 2,
                          color: "text.secondary",
                          fontSize: "14px",
                        }}
                      >
                        No data available
                      </Typography>
                    </StyledTableCell>
                  </TableRow>
                )}
              </TableBody>
            )}
          </Table>
        </TableContainer>
        <Box display="flex" justifyContent="center" mt={3}>
          <Pagination
            count={Math.ceil(totalCount / pageSize)}
            page={page}
            onChange={(event, value) => setPage(value)}
            color="primary"
            shape="rounded"
            variant="outlined"
          />
        </Box>
      </Box>
      {/* </Stack> */}
    </ComponentWrapper>
  );
}
